import { useCallback, useImperativeHandle, forwardRef } from 'react';
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';
import { useAtom } from 'jotai';

import { cn, urlObjectToUriString } from '@restworld/utils-common';

import { RestaurantJpStatusFilter, RestaurantLocationFilter } from './employer-restaurants-filters.components';
import {
  employerRestaurantJpStatusFilterAtom,
  employerRestaurantLocationFilterAtom,
} from '../employer-restaurants-filters/employer-restaurants.atoms';
import { useToggleEmployerFiltersModal } from '../../employer-filters-modal';
import { IEmployerFilterForm } from '../../job-listing/employer-job-listing-filters';
import { useV2Router } from '../../dashboard/hooks/use-v2-router';

interface EmployerRestuarantFiltersFormValues {
  jpStatus: string[];
  location: string;
}

const EmployerRestaurantFilters = forwardRef<IEmployerFilterForm>((_, ref) => {
  const methods = useForm<EmployerRestuarantFiltersFormValues>();
  const { handleSubmit } = methods;
  const [, setEmployerRestaurantJpStatusFilter] = useAtom(employerRestaurantJpStatusFilterAtom);
  const [, setEmployerRestaurantLocationFilter] = useAtom(employerRestaurantLocationFilterAtom);

  const { closeEmployerFiltersModal: closeModal } = useToggleEmployerFiltersModal();
  const router = useV2Router();
  const handleRemove = () => {
    setEmployerRestaurantJpStatusFilter([]);
    setEmployerRestaurantLocationFilter('');
    closeModal();
    router.push(urlObjectToUriString({ pathname: router.pathname, query: null }), null, { shallow: true });
  };

  const onSubmit: SubmitHandler<EmployerRestuarantFiltersFormValues> = useCallback(
    (values) => {
      setEmployerRestaurantJpStatusFilter(values.jpStatus);
      setEmployerRestaurantLocationFilter(values.location);
      router.push(urlObjectToUriString({ pathname: router.pathname, query: { ...values } }), null, { shallow: true });
      closeModal();
    },
    [closeModal, router, setEmployerRestaurantJpStatusFilter, setEmployerRestaurantLocationFilter]
  );

  useImperativeHandle(ref, () => ({
    applyFilters: handleSubmit(onSubmit),
    removeFilters: handleRemove,
  }));

  return (
    <FiltersWrapper>
      <FormProvider {...methods}>
        <form action="post" className="flex flex-col justify-between h-full gap-8 mt-8">
          <InputsWrapper>
            <RestaurantLocationFilter />
            <RestaurantJpStatusFilter />
          </InputsWrapper>
        </form>
      </FormProvider>
    </FiltersWrapper>
  );
});

export default EmployerRestaurantFilters;

export const computeRestaurantFiltersCount = (jpStatus?: string[], location?: string) => {
  let count = 0;
  if (jpStatus && jpStatus.length > 0) {
    count += 1;
  }
  if (location) {
    count += 1;
  }
  return count;
};

const FiltersWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col  h-full', className)} {...props} />
);
const InputsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-8', className)} {...props} />
);
